<template>
  <div>
    <data-table
      ref="datatableRef"
      :config="datatableConfig"
      :endpoint="endpoint"
      :refreshButton="refreshButtonConfig"
      :csvButton="csvButtonConfig"
      @data-loaded="handleDataLoaded"
      @data-error="handleDataError"
      @row-click="handleRowClick"
      @edit-click="handleEditClick"
      @button-click="handleButtonClick">
    </data-table>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, onMounted, onBeforeUnmount } from 'vue';
import { useI18n } from 'vue-i18n';
import { eventBus } from '../../eventBus';
import { log } from '../../utils/DebugLogger';
import { useFiltro } from '../../composables/useFiltro';
import DataTable from '../../grid/DataTable.vue';

type Id = number | string;

interface DataTableInstance {
  loadData: () => void;
}

interface CellParams {
  value?: unknown;
  data?: Record<string, unknown>;
}

interface Categoria {
  despesa_categoria_id: Id;
  [key: string]: unknown;
}

interface ButtonClickPayload {
  value?: unknown;
  data: Categoria;
  action: string;
}

const emit = defineEmits<{
  edit: [categoriaId: Id];
  delete: [categoria: Categoria];
}>();

const { t } = useI18n();
const { initialized } = useFiltro();

const datatableRef = ref<DataTableInstance | null>(null);

const endpoint = computed<string>(() => '/index.php/config/despesas_categorias/carrega_categorias');

const tableColumns = computed(() => {
  return [
    {
      data: 'despesa_categoria_id',
      title: t('ID'),
      width: 80,
      resizable: true,
      sortable: true,
      renderType: 'clickable_id'
    },
    {
      data: 'despesa_categoria_titulo',
      title: t('Título'),
      width: 250,
      resizable: true,
      sortable: true,
      flex: 1
    },
    {
      data: 'despesa_categoria_status',
      title: t('Status'),
      width: 120,
      resizable: true,
      sortable: true,
      cellRenderer: (params: CellParams): HTMLElement => {
        const status = params.value == 1 ? t('Ativo') : t('Inativo');
        const severity = params.value == 1 ? 'success' : 'secondary';

        const container = document.createElement('div');
        container.className = 'tag-container';
        container.innerHTML = `<span class="p-tag p-tag-${severity}">${status}</span>`;

        return container;
      }
    },
    {
      data: 'despesa_categoria_investimento',
      title: t('Investimento'),
      width: 130,
      resizable: true,
      sortable: true,
      cellRenderer: (params: CellParams): HTMLElement => {
        const investimento = params.value == 1 ? t('Sim') : t('Não');
        const severity = params.value == 1 ? 'info' : 'secondary';

        const container = document.createElement('div');
        container.className = 'tag-container';
        container.innerHTML = `<span class="p-tag p-tag-${severity}">${investimento}</span>`;

        return container;
      }
    },
    {
      data: 'despesa_categoria_percentual_padrao',
      title: t('Percentual Padrão'),
      width: 150,
      resizable: true,
      sortable: true,
      cellRenderer: (params: CellParams): string => {
        return params.value ? `${params.value}%` : '0%';
      }
    },
    {
      data: null,
      title: t('Ações'),
      width: 120,
      resizable: false,
      sortable: false,
      renderType: 'action_buttons',
      buttons: [
        {
          icon: 'pi pi-trash',
          class: 'p-button-danger',
          action: 'delete',
          tooltip: t('Excluir')
        }
      ]
    }
  ];
});

const datatableConfig = computed(() => {
  return {
    filename: `categorias_despesas_${new Date().toISOString().split('T')[0]}`,
    columns: tableColumns.value,
    rowTestId: (data: { despesa_categoria_id?: Id }) => {
      const id = data?.despesa_categoria_id;
      return id == null ? '' : `despesas-categorias-row-${id}`;
    },
    pageLength: 25,
    pagination: true,
    rowSelection: 'single',
    sortable: true,
    resizableColumns: true,
    showFilters: true,
    filterDisplay: 'row',
    stripedRows: true,
    showGridlines: true,
    size: 'small'
  };
});

const refreshButtonConfig = computed(() => {
  return {
    show: true,
    text: t('Atualizar')
  };
});

const csvButtonConfig = computed(() => {
  return {
    show: true,
    text: t('CSV')
  };
});

const loadData = (): void => {
  const stack = new Error().stack;
  log('DespesasCategoriasTable', 'loadData', 'Triggering table refresh', {
    callStack: stack?.split('\n').slice(1, 4).join('\n')
  });
  if (datatableRef.value) {
    datatableRef.value.loadData();
  }
};

const handleDataLoaded = (data: unknown[]): void => {
  log('DespesasCategoriasTable', 'handleDataLoaded', 'Data loaded', data.length, 'rows');
  eventBus.emit('globalMessage.success', t('Carregados') + ` ${data.length} ` + t('categorias'));
};

const handleDataError = (error: unknown): void => {
  log('DespesasCategoriasTable', 'handleDataError', 'Error loading data', error);
  const detail = error instanceof Error ? error.message : String(error);
  eventBus.emit('globalMessage.error', t('Erro ao carregar categorias: ') + detail);
};

const handleRowClick = (rowData?: { despesa_categoria_id?: Id }): void => {
  log('DespesasCategoriasTable', 'handleRowClick', 'Row clicked', rowData?.despesa_categoria_id || null);
};

const handleEditClick = (categoriaId: Id): void => {
  log('DespesasCategoriasTable', 'handleEditClick', 'Edit clicked for ID', categoriaId);
  emit('edit', categoriaId);
};

const handleButtonClick = ({ data, action }: ButtonClickPayload): void => {
  log('DespesasCategoriasTable', 'handleButtonClick', 'Button clicked', { action, data });
  if (action === 'delete') {
    emit('delete', data);
  }
};

const handleFilterUpdated = (): void => {
  log('DespesasCategoriasTable', 'event:filter.updated', 'Received');
  loadData();
};

const handleSaved = (): void => {
  log('DespesasCategoriasTable', 'event:despesas-categorias.saved', 'Received');
  loadData();
};

const handleDeleted = (): void => {
  log('DespesasCategoriasTable', 'event:despesas-categorias.deleted', 'Received');
  loadData();
};

const handleRefresh = (): void => {
  log('DespesasCategoriasTable', 'event:refresh-despesas-categorias', 'Received');
  loadData();
};

onMounted(() => {
  log('DespesasCategoriasTable', 'mounted', 'Setting up event listeners', {
    filterInitialized: initialized.value
  });

  log('DespesasCategoriasTable', 'mounted', 'Initial data load will be handled by DataTable autoLoad');

  eventBus.on('filter.updated', handleFilterUpdated);
  eventBus.on('despesas-categorias.saved', handleSaved);
  eventBus.on('despesas-categorias.deleted', handleDeleted);
  eventBus.on('despesas-categorias.refresh', handleRefresh);
});

onBeforeUnmount(() => {
  log('DespesasCategoriasTable', 'beforeUnmount', 'Cleaning up event listeners');

  eventBus.off('filter.updated', handleFilterUpdated);
  eventBus.off('despesas-categorias.saved', handleSaved);
  eventBus.off('despesas-categorias.deleted', handleDeleted);
  eventBus.off('despesas-categorias.refresh', handleRefresh);
});

defineExpose({
  loadData
});
</script>

<style scoped>
:deep(.tag-container) {
  display: flex;
  justify-content: center;
}

:deep(.p-tag) {
  padding: 0.25rem 0.5rem;
  font-size: 0.75rem;
  font-weight: 700;
  border-radius: 0.25rem;
}

:deep(.p-tag-success) {
  background-color: #00a65a;
  color: #fff;
}

:deep(.p-tag-secondary) {
  background-color: #d2d6de;
  color: #fff;
}

:deep(.p-tag-info) {
  background-color: #3c8dbc;
  color: #fff;
}

:deep(.action-buttons) {
  display: flex;
  gap: 5px;
  justify-content: center;
}

:deep(.p-button) {
  border-radius: 4px;
  transition: all 0.2s ease;
}

:deep(.p-button-danger) {
  background-color: #dd4b39;
  border-color: #dd4b39;
}

:deep(.p-button-danger:hover) {
  background-color: #d33724;
  border-color: #d33724;
}

:deep(.p-button-sm) {
  padding: 0.4rem 0.6rem;
  font-size: 0.875rem;
}

:deep(.p-button-icon-only) {
  width: 2rem;
  padding: 0.4rem;
}
</style>
